/**
 * screens/Settings — Finance
 * ──────────────────────────────────────────────────────────────────────────
 * Basics, feature setup, and defaults. Six cards, in the design's order:
 *
 *   1. Feature configuration   the four just-in-time gates
 *   2. Approval rules          WHEN → THEN
 *   3. Option lists            the five pickers
 *   4. Rules                   staleness and receipts
 *   5. Approval policy         the read-only quad
 *   6. Spend categories        code, name, GL, state
 *
 * EVERY WRITE RELOADS THE WORKSPACE, not just this screen. A policy save seeds
 * approval rules and can satisfy a gate, which changes what OTHER screens are
 * allowed to show — so the bootstrap is refetched rather than this component
 * patching its own copy and letting the rest of the app drift.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useState } from 'react';
import { Loading, PageContainer, PageHeader, Button } from '../../components';
import { useAsyncData } from '../../lib/useAsyncData';
import {
  deactivateApprovalRule,
  deactivateCategory,
  deactivateOption,
  fetchApprovalRules,
  patchPolicy,
  patchRules,
} from '../../services/config/config.service';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import type { ApprovalRule, Category, ListKind, OptionRow, StaleDays } from '../../schemas/config';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import ApprovalRulesCard from './components/ApprovalRulesCard';
import FeatureConfigCard from './components/FeatureConfigCard';
import OptionListsCard from './components/OptionListsCard';
import PolicyQuad from './components/PolicyQuad';
import RulesCard from './components/RulesCard';
import SettingsCard from './components/SettingsCard';
import SpendCategoriesCard from './components/SpendCategoriesCard';

export function SettingsScreen() {
  const { policy, rules, optionsByKind, categories, reload } = useConfig();
  const { dataVersion, invalidate, openModal } = useShell();
  const [busy, setBusy] = useState(false);

  // Approval rules are not part of the bootstrap payload — they change often
  // and only this screen and the engine care about the full list.
  const loadRules = useCallback(() => fetchApprovalRules(), []);
  const { data: approvalRules, loading } = useAsyncData(loadRules, [], {
    version: dataVersion,
    errorTitle: 'Could not load approval rules',
  });

  /**
   * One wrapper for every Settings write. It reloads the workspace AND bumps
   * the data version, because a configuration change can alter both what other
   * screens may show and what they would fetch.
   */
  const run = useCallback(
    async (action: () => Promise<unknown>, title: string, fallback: string) => {
      setBusy(true);
      try {
        const result = await action();
        showSuccess(result, title, fallback);
        await reload();
        invalidate();
      } catch (error) {
        handleApiError(error, `${title} failed`);
      } finally {
        setBusy(false);
      }
    },
    [reload, invalidate],
  );

  /* ── Feature configuration ─────────────────────────────────────────────── */

  const setVariance = () =>
    run(
      () => patchPolicy({ variance_set: true }),
      'Variance routing set',
      'A shortfall now posts to Payroll and a surplus to Billing.',
    );

  // Both connectors, never one: the sync log routes to Payroll AND Billing, and
  // half a connection leaves half the consequences stuck.
  const connectBoth = () =>
    run(
      () => patchPolicy({ connectors: ['payroll', 'billing'] }),
      'Payroll and Billing connected',
      'Consequences will now be delivered and their references recorded.',
    );

  const enableOffboardFeed = () =>
    run(
      () => patchPolicy({ offboard_feed: true }),
      'Offboarding feed connected',
      'Leavers holding company cash will now raise a settlement alert.',
    );

  /* ── Rules ─────────────────────────────────────────────────────────────── */

  const setStaleDays = (days: StaleDays) =>
    run(
      () => patchRules({ stale_days: days }),
      'Rule updated',
      `A count is overdue after ${days} days.`,
    );

  const setReceiptRequired = (required: boolean) =>
    run(
      () => patchRules({ receipt_required: required }),
      'Rule updated',
      required ? 'The spend form now asks for evidence.' : 'Evidence is now optional on spends.',
    );

  /* ── Removals ──────────────────────────────────────────────────────────── */

  const removeOption = (option: OptionRow) =>
    run(
      () => deactivateOption(option.id),
      `${option.display_name || option.value} removed`,
      'It has left the picker. Existing records keep it.',
    );

  const removeRule = (rule: ApprovalRule) =>
    run(
      () => deactivateApprovalRule(rule.id),
      'Rule removed',
      'Items already waiting keep the reason they were held for.',
    );

  const deactivateSpendCategory = (category: Category) =>
    run(
      () => deactivateCategory(category.id),
      `${category.code} deactivated`,
      'It will no longer be offered. Existing spends keep it.',
    );

  const addOptionValue = (kind: ListKind) => openModal({ kind: 'optionValue', listKind: kind });

  if (loading && !approvalRules) {
    return (
      <PageContainer width="settings">
        <Loading />
      </PageContainer>
    );
  }

  return (
    <PageContainer width="settings" testID="screen-settings">
      <PageHeader title="Settings" subtitle="Basics, feature setup, and defaults." />

      <FeatureConfigCard
        policy={policy}
        categoryCount={categories.filter((category) => category.is_active).length}
        onSetVariance={setVariance}
        onConnect={connectBoth}
        onEnableOffboardFeed={enableOffboardFeed}
        onAddCategories={() => openModal({ kind: 'category' })}
        busy={busy}
      />

      <ApprovalRulesCard
        rules={approvalRules ?? []}
        categories={categories}
        baseCurrency={policy?.base_currency ?? null}
        onAdd={() => openModal({ kind: 'approvalRule' })}
        onRemove={removeRule}
      />

      <OptionListsCard
        optionsByKind={optionsByKind}
        onAdd={addOptionValue}
        onRemove={removeOption}
      />

      <RulesCard
        rules={rules}
        onSetStaleDays={setStaleDays}
        onSetReceiptRequired={setReceiptRequired}
      />

      <SettingsCard
        title="Approval policy"
        action={
          <Button
            label="Edit"
            size="sm"
            variant="outline"
            onPress={() => openModal({ kind: 'approvalPolicy' })}
          />
        }
      >
        <PolicyQuad
          baseCurrency={policy?.base_currency ?? null}
          thresholdMinor={policy?.threshold_minor ?? null}
          autoUnderMinor={policy?.auto_under_minor ?? null}
          dualOverMinor={policy?.dual_over_minor ?? null}
        />
      </SettingsCard>

      <SpendCategoriesCard
        categories={categories}
        onAdd={() => openModal({ kind: 'category' })}
        onDeactivate={deactivateSpendCategory}
      />
    </PageContainer>
  );
}

export default SettingsScreen;
